/**
 * Presentation helpers: labels, clue text, and animation geometry. Nothing here decides
 * a game rule — the engine owns those — it only describes the model for the screen.
 */
import { type GameState, type PuzzleModel } from '@arrow/game-engine';
import type { FlowState } from '@arrow/game-engine/flow';
import type { Clue, ClueContent } from '@arrow/puzzle-schema';

import type { GameAction } from './keyboard.ts';

export function clueContentText(content: ClueContent): string {
	switch (content.kind) {
		case 'text':
			return content.text;
		case 'icon':
			return content.label;
		case 'image':
			return content.alt;
	}
}

export function clueForAnswer(model: PuzzleModel, answerId: string): Clue | undefined {
	const answer = model.answers[answerId];
	return answer ? model.clues[answer.clueId] : undefined;
}

export function clueText(model: PuzzleModel, answerId: string): string {
	const clue = clueForAnswer(model, answerId);
	return clue ? clueContentText(clue.content) : '';
}

/** "Across, 3 letters: Glide on snow" */
export function answerDescription(model: PuzzleModel, answerId: string): string {
	const answer = model.answers[answerId];
	if (!answer) return '';
	return `${answer.axis === 'across' ? 'Across' : 'Down'}, ${answer.cells.length} letters: ${clueText(model, answerId)}`;
}

/**
 * A cell's accessible name. What's in it comes first (a screen reader user moving through
 * the grid, or typing, hears the letter before the address), then where it is, then the
 * answers it belongs to. A clue cell leads with its clue, so its name starts with the
 * words printed in it (WCAG 2.5.3).
 *
 * `short`: the cursor cell while typing within one answer. Its place in that answer is
 * enough ("Letter S, 2 of 3"); the whole crossing again on every key is noise (A11Y-14).
 * A jump (a tap, an arrow, a new answer) gets the full name.
 */
export function cellLabel(model: PuzzleModel, state: GameState, cell: number, solved: ReadonlySet<string>, short = false): string {
	const cur = short && state.cursor ? model.answers[state.cursor.answerId] : undefined;
	if (cur && cur.cells.includes(cell) && model.kinds[cell] === 'letter') {
		const letter = state.entries[cell];
		const parts = [letter ? `Letter ${letter}` : 'Empty'];
		if (state.flaggedCells.includes(cell)) parts.push('checked and wrong');
		parts.push(`${cur.cells.indexOf(cell) + 1} of ${cur.cells.length}`);
		return parts.join(', ');
	}
	const row = Math.floor(cell / model.width) + 1;
	const col = (cell % model.width) + 1;
	const where = `row ${row}, column ${col}`;
	const kind = model.kinds[cell];
	if (kind === 'clue') {
		const clues = (model.cluesByCell[cell] ?? []).map((id) => model.clues[id]).filter((c): c is Clue => !!c);
		const parts = clues.map((c) => `${clueContentText(c.content)}, ${ARROW_WORDS[c.direction]}`);
		return `Clue: ${parts.join('; ')}. ${capitalise(where)}`;
	}
	if (kind !== 'letter') return `Blank, ${where}`;
	const letter = state.entries[cell];
	const parts = [letter ? `Letter ${letter}` : 'Empty'];
	if (state.flaggedCells.includes(cell)) parts.push('checked and wrong');
	parts.push(where);
	const answers = (model.answersByCell[cell] ?? []).map((id) => `${answerDescription(model, id)}${solved.has(id) ? ' (solved)' : ''}`);
	return [parts.join(', '), ...answers].join('. ');
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * What a drawn key did, for a screen reader (the key keeps focus, so nothing else says it):
 * a letter and where it went ("S, 2 of 5"), a deletion ("Deleted, 3 of 5"), or after the
 * swap key the answer you're on ("Down, Prehistoric relic, 6"). Null when nothing changed.
 */
export function keyEcho(model: PuzzleModel, action: GameAction, before: GameState, after: GameState): string | null {
	if (action.type === 'toggleDirection') {
		const answer = after.cursor ? model.answers[after.cursor.answerId] : undefined;
		if (!answer || !after.cursor) return null;
		return `${answer.axis === 'across' ? 'Across' : 'Down'}, ${clueText(model, after.cursor.answerId)}, ${answer.cells.length}`;
	}
	if (action.type !== 'letter' && action.type !== 'backspace') return null;
	const cell = after.entries.findIndex((e, i) => e !== before.entries[i]);
	if (cell < 0) return null;
	const ids = [before.cursor?.answerId, after.cursor?.answerId, ...(model.answersByCell[cell] ?? [])];
	const answer = ids.map((id) => (id ? model.answers[id] : undefined)).find((a) => a?.cells.includes(cell));
	if (!answer) return null;
	const where = `${answer.cells.indexOf(cell) + 1} of ${answer.cells.length}`;
	return action.type === 'letter' ? `${after.entries[cell]}, ${where}` : `Deleted, ${where}`;
}

const ARROW_WORDS = {
	right: 'answer runs right',
	down: 'answer runs down',
	'right-down': 'answer starts to the right and runs down',
	'down-right': 'answer starts below and runs right',
} as const;

/**
 * Steps from `origin` to every letter cell, walking only along answers (cell to
 * neighbouring cell within an answer, switching answers at crossings). Drives the
 * completion wave so it travels the way the puzzle is connected.
 */
export function waveDistances(model: PuzzleModel, origin: number): Map<number, number> {
	const dist = new Map<number, number>([[origin, 0]]);
	const queue = [origin];
	while (queue.length > 0) {
		const cell = queue.shift()!;
		const d = dist.get(cell)!;
		for (const id of model.answersByCell[cell] ?? []) {
			const cells = model.answers[id]!.cells;
			const i = cells.indexOf(cell);
			for (const j of [i - 1, i + 1]) {
				const next = cells[j];
				if (next === undefined || dist.has(next)) continue;
				dist.set(next, d + 1);
				queue.push(next);
			}
		}
	}
	// Letter cells not connected to the origin still take part, at the end.
	const max = Math.max(0, ...dist.values());
	for (const cell of model.letterCells) if (!dist.has(cell)) dist.set(cell, max + 1);
	return dist;
}

/** Where a clue sits in its clue cell: slot 0 or 1 of `count`. */
export function clueSlot(model: PuzzleModel, clue: Clue): { cell: number; slot: number; count: number } {
	const cell = clue.cell.row * model.width + clue.cell.col;
	const ids = model.cluesByCell[cell] ?? [clue.id];
	return { cell, slot: Math.max(0, ids.indexOf(clue.id)), count: ids.length };
}

/** "Wed 22 Oct" for a YYYY-MM-DD key, read as a local calendar date. */
export function formatDateKey(key: string, locale?: string): string {
	const [y, m, d] = key.split('-').map(Number);
	if (!y || !m || !d) return key;
	return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(y, m - 1, d));
}

/** "Wednesday" for a YYYY-MM-DD key, read as a local calendar date. */
export function weekdayName(key: string | null, locale = 'en-GB'): string {
	const date = keyToDate(key);
	return date ? new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date) : '';
}

/** "21 October" for a YYYY-MM-DD key, read as a local calendar date. */
export function dayMonth(key: string | null, locale = 'en-GB'): string {
	const date = keyToDate(key);
	return date ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date) : '';
}

/** "monday" … "sunday": the daily's art-direction key (never a game rule). */
export function weekdayKey(key: string | null): string {
	return weekdayName(key, 'en-GB').toLowerCase();
}

function keyToDate(key: string | null): Date | null {
	if (!key) return null;
	const [y, m, d] = key.split('-').map(Number);
	return y && m && d ? new Date(y, m - 1, d) : null;
}

/* ---- Board geometry, in units of U per cell (the overlay's viewBox) ------------------ */

export const U = 100;

export type ArrowShape = 'right' | 'down' | 'right-down' | 'down-right';

/**
 * Where a clue's arrow sits: on the boundary between its clue block and the answer's
 * first cell, at the clue's half of the cell. The wedge grows out of the clue into
 * the answer.
 */
export function arrowAnchor(model: PuzzleModel, clue: Clue): { x: number; y: number; shape: ArrowShape } {
	const { slot, count } = clueSlot(model, clue);
	const { row, col } = clue.cell;
	const midY = row * U + (count === 1 ? U / 2 : slot === 0 ? U / 4 : (U * 3) / 4);
	switch (clue.direction) {
		case 'right':
			return { x: (col + 1) * U, y: midY, shape: 'right' };
		case 'right-down':
			return { x: (col + 1) * U, y: midY, shape: 'right-down' };
		case 'down':
			return { x: col * U + U / 2, y: (row + 1) * U, shape: 'down' };
		case 'down-right':
			return { x: col * U + U / 2, y: (row + 1) * U, shape: 'down-right' };
	}
}

export function cellCentre(model: PuzzleModel, cell: number): [number, number] {
	return [(cell % model.width) * U + U / 2, Math.floor(cell / model.width) * U + U / 2];
}

/**
 * A Flow hand-off between two answers that cross: the shared cell, a stitch through
 * both (the longer side of the earlier answer into the crossing, then on along the
 * longer side of the new one), and where the "FLOW ×n" tag emerges (the stitch's end).
 */
export function flowStitch(model: PuzzleModel, prevId: string, nextId: string) {
	const prev = model.answers[prevId]?.cells;
	const next = model.answers[nextId]?.cells;
	if (!prev || !next) return null;
	const cross = next.find((c) => prev.includes(c));
	if (cross === undefined) return null;
	const pi = prev.indexOf(cross);
	const ni = next.indexOf(cross);
	const pseg = pi >= prev.length - 1 - pi ? prev.slice(0, pi + 1) : prev.slice(pi).reverse();
	const nseg = ni <= next.length - 1 - ni ? next.slice(ni) : next.slice(0, ni + 1).reverse();
	const cells = [...pseg, ...nseg.slice(1)];
	return { cross, cells, end: cells[cells.length - 1]! };
}

/** Every answer's place in the solve (Flow chains in order, then anything else solved). */
export function solveOrder(model: PuzzleModel, game: GameState, flow: FlowState): Map<string, number> {
	const order = new Map<string, number>();
	const add = (id: string) => {
		if (!order.has(id) && model.answers[id]) order.set(id, order.size);
	};
	for (const segment of flow.segments) segment.forEach(add);
	game.solvedAnswers.forEach(add);
	model.answerOrder.forEach(add);
	return order;
}

/** The longest Flow chain of the solve (the first, on a tie). */
export function bestChain(flow: FlowState): string[] {
	return flow.segments.reduce<string[]>((best, s) => (s.length > best.length ? s : best), []);
}
